/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { AddWhite, ChevronRightIcon, CloseIcon, DoneIcon, EditIcon, SearchIcon, TrashIcon } from '../../../assets/icons';
import { classNames, Constants, sanitizeToNumbers } from '../../../utils';
import { Input, Modal } from '../../../components';
import { MaintenancePlanService } from '../../../services';
import ModalCreateSubcategory from './modal-create-subcategory';

const IT = Constants.MAINTENANCE.INTERVAL_TYPE;
const IU = Constants.MAINTENANCE.INTERVAL_UNIT;

const getIntervalUnit = (unit) => {
  switch (unit) {
    case IU.DAY: return 'days';
    case IU.WEEK: return 'weeks';
    case IU.MONTH: return 'months';
    case IU.YEAR: return 'years';
    default: return 'years';
  }
}

const MaintenancePlan = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const { form, isLoading, itemsToDelete, isDeleting, onChange, onChangeName, removeItems, selects, setItemsToDelete, setIsDeleting, submit, updateSubcategories } = useForm();

  const filteredItems = useMemo(() => {
    if (!search) return form.items;
    return form.items.filter(x => x.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  }, [search, form.items]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-md p-8 flex items-center justify-center min-h-[400px]">
          <div className="text-text font-medium">Cargando categoría...</div>
        </div>
      </div>
    );
  }

  return (
    <main className="w-full">
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalCreateSubcategory
          initialData={form}
          onClose={() => setIsModalOpen(false)}
          onSuccess={updateSubcategories}
        />
      </Modal>

      <div className="bg-orange rounded-lg px-3 py-2 max-w-96 transition-all">
        <div className="flex items-center justify-between gap-4">
          {isEditingName ? (
            <input
              type="text"
              autoFocus
              className="text-lg text-black px-2 py-1 rounded w-full outline-none"
              value={form.name}
              onChange={(e) => onChangeName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
            />
          ) : (
            <p
              className="text-lg text-white cursor-pointer w-full truncate"
              onClick={() => navigate('/maintenance-plan')}
              title="Volver a la lista"
            >
              { form.name }
            </p>
          )}
          <div className="flex items-center justify-end gap-3 shrink-0">
            <img
              src={EditIcon}
              className="w-5 h-5 object-contain cursor-pointer brightness-0 invert"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingName(true);
              }}
              alt="Editar"
            />
            <img
              src={TrashIcon}
              className="w-5 h-5 object-contain cursor-pointer brightness-0 invert"
              onClick={(e) => {
                e.stopPropagation();
              }}
              alt="Eliminar"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden mt-6">

        <p className="text-2xl font-bold py-4 pl-3">{ form.name }</p>
        <div className="flex items-center pb-4 px-3 gap-4">
          <p className="text-2xl font-bold text-default">Subcategorías</p>

          <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 bg-green text-white px-4 py-1.5 rounded-lg font-semibold">
            <img src={AddWhite} className="w-4 h-4" alt="" />
            Nueva
          </button>

          <Input
            className={{ base: 'max-w-80', wrapper: 'rounded-lg' }}
            placeholder="Nombre subcategoría"
            value={search}
            onValueChange={setSearch}
            prepend={
              <img src={SearchIcon} className="w-4 h-4" alt="" />
            }
            append={
              <img src={CloseIcon} className="w-3 h-3 opacity-50 cursor-pointer" alt="" onClick={() => setSearch('')} />
            }
          />
        </div>

        <div className="overflow-x-auto p-3">
          <div className="flex items-center justify-between">
            <p>Lista de Subcategorías</p>
            <div>
              {isDeleting ? (
                <button
                  onClick={() => itemsToDelete.length === form.items.length ? setItemsToDelete([]) : setItemsToDelete(form.items.map(x => x._id))}
                  className={classNames(
                    'px-4 py-1.5 border font-bold rounded-lg',
                    itemsToDelete.length === form.items.length ? 'border-green bg-green/10 text-black' : 'border-black text-default',
                  )}
                >
                  Seleccionar todo
                </button>
              ) : (
                <button
                  onClick={() => setIsDeleting(true)}
                  className="px-4 py-1.5 border border-default text-default font-bold rounded-lg transition-all hover:bg-red/10 hover:text-red hover:border-red/50"
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>

          <div>
            {filteredItems?.map(sub => (
              <div key={sub._id}>
                <ItemHeader
                  item={sub}
                  isDeleting={isDeleting}
                  markedForDeletion={itemsToDelete.includes(sub._id)}
                  toggleOpen={() => onChange(sub._id,  'isOpen', !sub.isOpen)}
                  toggleDelete={() => itemsToDelete.includes(sub._id)
                    ? setItemsToDelete(s => s.filter(x => x !== sub._id))
                    : setItemsToDelete(s => [...s, sub._id])
                  }
                />

                {sub.isOpen && (
                  <div>
                    <p className="font-semibold text-lg my-3">Medida de control</p>

                    <div className="grid grid-cols-3">
                      {selects.interval_types.map((type, index) => (
                        <Checkbox
                          key={index}
                          label={type.label}
                          isActive={type.value === sub.trigger_criteria}
                          onChange={() => onChange(sub._id, 'trigger_criteria', type.value)}
                        />
                      ))}
                    </div>

                    <hr className="my-3 border-default" />

                    {[IT.BOTH, IT.MILEAGE].includes(sub?.trigger_criteria) && (
                      <>
                        <p className="text-2xl text-orange font-bold">Kilometraje</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="w-96">
                            <p className="font-semibold">Intervalo</p>
                            <p>Define cada cuántos kilómetros se debe realizar este mantenimiento</p>
                            <Input
                              className={{ wrapper: 'rounded-lg' }}
                              placeholder="5.000"
                              value={sub.mileage_interval}
                              onValueChange={v => onChange(sub._id, 'mileage_interval', sanitizeToNumbers(v))}
                              append={<span className="text-default">Km</span>}
                            />
                          </div>

                          <div className="w-96">
                            <p className="font-semibold">Alerta anticipada</p>
                            <p>Establece con cuántos kilómetros de anticipación deseas recibir alerta de mantenimiento</p>
                            <Input
                              className={{ wrapper: 'rounded-lg' }}
                              placeholder="500"
                              value={sub.mileage_alert}
                              onValueChange={v => onChange(sub._id, 'mileage_alert', sanitizeToNumbers(v))}
                              append={<span className="text-default">Km</span>}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {[IT.BOTH, IT.TIME].includes(sub?.trigger_criteria) && (
                      <>
                        <p className="text-2xl text-orange font-bold mt-4">Tiempo</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="w-96">
                            <p className="font-semibold">Intervalo</p>
                            <div className="flex gap-4">
                              <IntervalUnitButton
                                label="Días"
                                isActive={sub.time_interval_unit === Constants.MAINTENANCE.INTERVAL_UNIT.DAY}
                                onClick={() => onChange(sub._id, 'time_interval_unit', Constants.MAINTENANCE.INTERVAL_UNIT.DAY)}
                              />
                              <IntervalUnitButton
                                label="Semanas"
                                isActive={sub.time_interval_unit === Constants.MAINTENANCE.INTERVAL_UNIT.WEEK}
                                onClick={() => onChange(sub._id, 'time_interval_unit', Constants.MAINTENANCE.INTERVAL_UNIT.WEEK)}
                              />
                              <IntervalUnitButton
                                label="Meses"
                                isActive={sub.time_interval_unit === Constants.MAINTENANCE.INTERVAL_UNIT.MONTH}
                                onClick={() => onChange(sub._id, 'time_interval_unit', Constants.MAINTENANCE.INTERVAL_UNIT.MONTH)}
                              />
                              <IntervalUnitButton
                                label="Años"
                                isActive={sub.time_interval_unit === Constants.MAINTENANCE.INTERVAL_UNIT.YEAR}
                                onClick={() => onChange(sub._id, 'time_interval_unit', Constants.MAINTENANCE.INTERVAL_UNIT.YEAR)}
                              />
                            </div>
                            <Input
                              className={{ base: 'max-w-96', wrapper: 'rounded-lg' }}
                              value={sub.time_interval}
                              onValueChange={v => onChange(sub._id, 'time_interval', sanitizeToNumbers(v))}
                              append={<IntervalUnit id={sub.time_interval_unit} />}
                            />
                          </div>

                          <div className="w-96">
                            <p className="font-semibold">Alerta anticipada</p>
                            <p>Establece con cuántos días de llegar al intervalo quieres recibir la alerta</p>
                            <Input
                              className={{ wrapper: 'rounded-lg' }}
                              placeholder="5"
                              value={sub.time_alert}
                              onValueChange={v => onChange(sub._id, 'time_alert', sanitizeToNumbers(v))}
                              append={<IntervalUnit id={Constants.MAINTENANCE.INTERVAL_UNIT.DAY} />}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {!form?.items?.length && (
            <p className="py-8 text-text text-center">Sin datos para mostrar</p>
          )}

          {isDeleting ? (
            <div className="flex items-center gap-4">
              <button
                onClick={removeItems}
                className={classNames('bg-red px-10 py-2 rounded-full text-white font-bold mt-8')}
              >
                Eliminar ({ itemsToDelete.length })
              </button>

              <button
                onClick={() => {
                  setIsDeleting(false);
                  setItemsToDelete([]);
                }}
                className={classNames('px-10 py-2 rounded-full font-bold mt-8')}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              onClick={submit}
              className={classNames('bg-orange px-10 py-2 rounded-full text-white font-bold mt-8')}
            >
              Guardar
            </button>
          )}
        </div>
      </div>
    </main>
  );
};

const ItemHeader = ({ item, isDeleting, markedForDeletion, toggleDelete, toggleOpen }) => {
  const isFilled = useMemo(() => isValidSubcategory(item, false), [item]);
  return (
    <div
      onClick={toggleOpen}
      className={classNames(
        'flex items-center border gap-4 px-4 py-2 rounded-lg mt-4 cursor-pointer transition-all',
        item.isOpen && !markedForDeletion ? 'bg-orange border-orange text-white' : 'border-default',
        markedForDeletion ? 'bg-red/10 border-red/50' : ''
      )}
    >
      {isDeleting ? (
        <div
          onClick={e => {
            e.stopPropagation();
            toggleDelete();
          }}
          className={classNames(
            'w-4 h-4 rounded-full flex items-center justify-center border',
            markedForDeletion ? 'bg-red border-red' : 'border-default bg-gray5'
          )}
        />
      ) : (
        <div
          className={classNames(
            'w-4 h-4 rounded-full flex items-center justify-center border',
            isFilled ? 'bg-green border-green' : 'border-default'
          )}
        >
          {isFilled && (
            <img src={DoneIcon} className="w-3 h-3 object-contain invert" alt="" />
          )}
        </div>
      )}

      <div className="flex flex-1 justify-between items-center">
        <p className="font-semibold">{ item.name }</p>
        <img
          src={ChevronRightIcon}
          className={classNames(
            'w-4 h-4 object-contain cursor-pointer transition-all brightness-0',
            item.isOpen ? '-rotate-90 invert' : 'rotate-90'
          )}
          alt=""
        />
      </div>
    </div>
  )
}

const Checkbox = ({ label, isActive, onChange }) => (
  <div className="flex items-center gap-2 cursor-pointer max-w-min" onClick={onChange}>
    <div
      className={classNames(
        'w-4 h-4 rounded-full flex items-center justify-center border',
        isActive ? 'bg-green border-green' : 'border-default'
      )}
    />
    <span>{ label }</span>
  </div>
)

const IntervalUnitButton = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={classNames(
      'my-2 px-4 py-1.5 rounded-lg font-semibold transition-all',
      isActive ? 'bg-orange text-white' : 'bg-[#F5F7FA]'
    )}
  >
    { label }
  </button>
)

const IntervalUnit = ({ id }) => {
  const label = useMemo(() => {
    if (id === Constants.MAINTENANCE.INTERVAL_UNIT.DAY) return 'Días';
    if (id === Constants.MAINTENANCE.INTERVAL_UNIT.WEEK) return 'Semanas';
    if (id === Constants.MAINTENANCE.INTERVAL_UNIT.MONTH) return 'Meses';
    if (id === Constants.MAINTENANCE.INTERVAL_UNIT.YEAR) return 'Años';
  }, [id]);

  return (
    <span className="text-default">{ label }</span>
  )
}

const isValidSubcategory = (item, showError = true) => {
  const onError = (msg) => {
    if (showError) toast.error(msg);
    return false;
  }

  if (!item.trigger_criteria)
    return onError(`${item.name}: La medida de control es requerida `);

  if ([IT.BOTH, IT.MILEAGE].includes(item.trigger_criteria)) {
    if (!item.mileage_interval)
      return onError(`${item.name}: El intervalo (Kilometraje) es requerido`);

    if (item.mileage_interval <= 0)
      return onError(`${item.name}: El intervalo (Kilometraje) debe ser mayor a cero`);

    if (!item.mileage_alert)
      return onError(`${item.name}: La alerta anticipada (Kilometraje) es requerida`);

    if (item.mileage_alert <= 0)
      return onError(`${item.name}: La alerta anticipada (Kilometraje) debe ser mayor a cero`);

    if (item.mileage_alert >= item.mileage_interval)
      return onError(`${item.name}: La alerta anticipada (Kilometraje) debe ser menor al intervalo`);
  }

  if ([IT.BOTH, IT.TIME].includes(item.trigger_criteria)) {
    if (!item.time_interval_unit)
      return onError(`${item.name}: La unidad de intervalo (Tiempo) debe ser una opción válida (Días, Semanas, Meses, Años)`);

    if (!item.time_interval)
      return onError(`${item.name}: El intervalo (Tiempo) es requerido`);

    if (item.time_interval <= 0)
      return onError(`${item.name}: El intervalo (Tiempo) debe ser mayor a cero`);

    if (item.time_alert <= 0)
      return onError(`${item.name}: La alerta anticipada (Tiempo) es requerida`);

    if (item.time_alert <= 0)
      return onError(`${item.name}: La alerta anticipada (Tiempo) debe ser mayor a cero`);

    const intervalToDays = Math.abs(moment().diff(moment().add(item.time_interval, getIntervalUnit(item.time_interval_unit)), 'days'));
    if (item.time_alert >= intervalToDays)
      return onError(`${item.name}: La alerta anticipada (Tiempo) debe ser menor al intervalo`);
  }

  return true;
}

const useForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    id: '',
    name: '',
    items: [],
  });
  const [selects, setSelects] = useState({
    interval_types: [],
    interval_units: [],
  });
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getSelects = async () => {
    try {
      setSelects({
        interval_types: [
          { value: Constants.MAINTENANCE.INTERVAL_TYPE.MILEAGE, label: 'Kilometraje' },
          { value: Constants.MAINTENANCE.INTERVAL_TYPE.TIME, label: 'Tiempo' },
          { value: Constants.MAINTENANCE.INTERVAL_TYPE.BOTH, label: 'Ambos' },
        ],
        interval_units: [
          { value: Constants.MAINTENANCE.INTERVAL_UNIT.DAY, label: 'Días' },
          { value: Constants.MAINTENANCE.INTERVAL_UNIT.WEEK, label: 'Semanas' },
          { value: Constants.MAINTENANCE.INTERVAL_UNIT.MONTH, label: 'Meses' },
          { value: Constants.MAINTENANCE.INTERVAL_UNIT.YEAR, label: 'Años' },
        ],
      });

    } catch (error) {
      setSelects([]);
    }
  }

  const fillForm = async () => {
    setIsLoading(true);

    try {
      const decodedId = atob(id);
      const category = await MaintenancePlanService.admin.findOne(decodedId);
      setForm(s => ({
        ...s,
        id: category.id,
        name: category.name,
        items: category.subcategories?.map(x => ({
          ...x,
          isOpen: false,
          _id: uuid(),
        })) || [],
      }));

    } catch (error) {
      toast.error('Error al cargar la categoría');
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (_id, target, value) => {
    const items = form.items.map(item => {
      if (item._id === _id) {
        return { ...item, [target]: value };
      }
      return item;
    });

    setForm(s => ({ ...s, items }));
  };

  const onChangeName = (name) => {
    setForm(s => ({ ...s, name }));
  };

  const updateSubcategories = (_form) => {
    const currentIds = form.items.map(x => x._id);
    const updatedItems = _form.items.filter(x => currentIds.includes(x._id));
    const newItems = _form.items.filter(x => !currentIds.includes(x._id));
    const deletedItems = form.items.filter(x => !_form.items.some(y => y._id === x._id));

    const newComposition = form.items
      // Elimina los que no llegaron del modal
      .filter(x => !deletedItems.some(y => y._id === x._id))
      // Modifica los que llegaron del modal
      .map(x => {
        const item = updatedItems.find(y => y._id === x._id);
        if (!item) return x;

        return {
          ...x,
          name: item.name,
        };
      })
      // Agrega los que llegaron del modal
      .concat(newItems);

    setForm(s => ({ ...s, items: newComposition }));
  }

  const removeItems = () => {
    const others = form.items.filter(x => !itemsToDelete.includes(x._id));
    setForm(s => ({ ...s, items: others }));
    setItemsToDelete([]);
    setIsDeleting(false);
  }

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    };

    // if (!form.items.length)
    //   return onError('Debe agregar al menos una subcategoría');

    const item = form.items.find(x => form.items.filter(y => String(y.name).toLocaleLowerCase() === String(x.name).toLocaleLowerCase())?.length > 1);
    if (item)
      return onError(`No puede haber subcategorías duplicadas: ${item.name}`);

    return !form.items.some(x => !isValidSubcategory(x));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      await MaintenancePlanService.admin.update(form);
      toast.success('Plan de mantenimiento actualizado con éxito');
      navigate('/maintenance-plan');

    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSelects();
    if (!id) return;

    fillForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return {
    form,
    isLoading,
    isDeleting,
    itemsToDelete,
    onChange,
    onChangeName,
    removeItems,
    selects,
    setItemsToDelete,
    setIsDeleting,
    submit,
    updateSubcategories,
  };
};

export default MaintenancePlan;